/**
 * SnowflakeCredentialSection — portfolio-admin surface for the connector's
 * portfolio-wide credential: list existing credentials by hint, mint a new
 * one (shown once), and revoke.
 */

import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import type { BadgeColors } from '@/lib/base/badges/badge-types';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { EmptyState } from '@/lib/base/empty-state/empty-state';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import { Table } from '@/lib/base/table/table';
import { QueryErrorState } from '@/lib/components/QueryErrorState';
import type { WebhookSecretCreatedData } from '@/lib/crm/components/webhooks/WebhookSecretCreatedModal';
import { WebhookSecretCreatedModal } from '@/lib/crm/components/webhooks/WebhookSecretCreatedModal';
import { logger } from '@/lib/logger';
import { getPortfolioActionError } from '@/lib/portfolio/errors';
import { useCreateSnowflakeCredential } from '@/lib/portfolio/hooks/useCreateSnowflakeCredential';
import { useRevokeSnowflakeCredential } from '@/lib/portfolio/hooks/useRevokeSnowflakeCredential';
import { useSnowflakeCredentials } from '@/lib/portfolio/hooks/useSnowflakeCredentials';
import type { SnowflakeDirectoryCredential } from '@/lib/portfolio/snowflake-types';

// WebhookSecretCreatedModal already renders "This token is shown once. You won't be able to
// see it again." as its description, so this line says only what that one can't: which credential
// this is, and what to do if it's lost.
const SECRET_MODAL_INSTRUCTION =
  "This is the Snowflake connector credential. If it's lost, create a new one.";

const REVOKE_DIALOG_DESCRIPTION =
  'The connector will no longer be able to authenticate with this credential.';

const TABLE_COLUMNS = [
  { id: 'credential', label: 'Credential' },
  { id: 'created', label: 'Created' },
  { id: 'status', label: 'Status' },
  { id: 'action', label: 'Action' },
] as const;

type CredentialStatus = 'active' | 'revoked';

const CREDENTIAL_STATUS_CONFIG: Record<
  CredentialStatus,
  { label: string; color: BadgeColors }
> = {
  active: { label: 'Active', color: 'success' },
  revoked: { label: 'Revoked', color: 'error' },
};

export interface SnowflakeCredentialSectionProps {
  portfolioId: number;
}

interface CredentialRowProps {
  credential: SnowflakeDirectoryCredential;
  displayTimezone: string;
  onRevoke: (credentialId: number) => void;
}

/**
 * Wraps the "Revoke" button so each row gets a stable click handler (per
 * FRONTEND_PATTERNS.md's stable-reference rule) without the parent needing
 * to recreate a per-credential callback on every render.
 */
function CredentialRow({
  credential,
  displayTimezone,
  onRevoke,
}: CredentialRowProps) {
  const handleRevokeClick = useCallback(() => {
    onRevoke(credential.id);
  }, [credential.id, onRevoke]);

  const status: CredentialStatus = credential.is_revoked ? 'revoked' : 'active';
  const { label, color } = CREDENTIAL_STATUS_CONFIG[status];

  return (
    <Table.Row>
      <Table.Cell>
        <span className='font-mono text-body-small text-[var(--color-text-primary)]'>
          {credential.token_hint}
        </span>
      </Table.Cell>
      <Table.Cell>
        <span className='text-body-small text-[var(--color-text-tertiary)]'>
          {/* The month is written out and the locale is explicit: leaving the
              locale to the browser rendered the same credential as 04/09/2026 on
              one machine and 9/4/2026 on another, which is unreadable next to a
              second credential created a week apart. */}
          {new Date(credential.created_at).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            timeZone: displayTimezone,
          })}
        </span>
      </Table.Cell>
      <Table.Cell>
        <Badge type='color' size='sm' color={color}>
          {label}
        </Badge>
      </Table.Cell>
      <Table.Cell>
        {status === 'active' && (
          <Button size='xs' color='tertiary' onClick={handleRevokeClick}>
            Revoke
          </Button>
        )}
      </Table.Cell>
    </Table.Row>
  );
}

export function SnowflakeCredentialSection({
  portfolioId,
}: SnowflakeCredentialSectionProps) {
  const displayTimezone = useDisplayTimezone();
  const credentialsQuery = useSnowflakeCredentials(portfolioId);
  const createMutation = useCreateSnowflakeCredential(portfolioId);
  const revokeMutation = useRevokeSnowflakeCredential(portfolioId);

  const [secretData, setSecretData] = useState<WebhookSecretCreatedData | null>(
    null
  );
  const [revokeCredentialId, setRevokeCredentialId] = useState<number | null>(
    null
  );

  const handleCloseSecretModal = useCallback(() => {
    setSecretData(null);
  }, []);

  // Guard against a double-set: keep the first minted secret if one is
  // somehow reported twice before the modal is dismissed.
  const handleSecretMinted = useCallback((data: WebhookSecretCreatedData) => {
    setSecretData(prev => prev ?? data);
  }, []);

  // The create mutation is owned here, in the always-mounted section, so the
  // one-time token display is never dropped regardless of what else on the
  // page mounts or unmounts.
  const handleCreate = useCallback(() => {
    createMutation.mutate(undefined, {
      onSuccess: res => {
        handleSecretMinted({
          bearer_token: res.token,
          secret_hint: res.hint,
          secret_entity_id: '',
        });
      },
      onError: err => {
        logger.error(
          "Couldn't create Snowflake connector credential",
          err instanceof Error ? err : new Error(String(err)),
          { component: 'portfolio', portfolio_id: portfolioId }
        );
        toast.error(
          `Couldn't create a connector credential: ${getPortfolioActionError(err)}`
        );
      },
    });
  }, [createMutation, handleSecretMinted, portfolioId]);

  const handleRequestRevoke = useCallback((credentialId: number) => {
    setRevokeCredentialId(credentialId);
  }, []);

  const handleCancelRevoke = useCallback(() => {
    setRevokeCredentialId(null);
  }, []);

  const handleConfirmRevoke = useCallback(async () => {
    if (revokeCredentialId === null) {
      return;
    }
    try {
      await revokeMutation.mutateAsync(revokeCredentialId);
      setRevokeCredentialId(null);
    } catch (err) {
      logger.error(
        "Couldn't revoke Snowflake connector credential",
        err instanceof Error ? err : new Error(String(err)),
        {
          component: 'portfolio',
          portfolio_id: portfolioId,
          credential_id: revokeCredentialId,
        }
      );
      toast.error(
        `Couldn't revoke this credential: ${getPortfolioActionError(err)}`
      );
    }
  }, [portfolioId, revokeCredentialId, revokeMutation]);

  const { refetch } = credentialsQuery;
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const credentials = credentialsQuery.data ?? [];

  let body;
  if (credentialsQuery.isLoading) {
    body = <LoadingIndicator size='sm' />;
  } else if (credentialsQuery.isError) {
    body = (
      <QueryErrorState
        error={credentialsQuery.error}
        label="Couldn't load connector credentials"
        onRetry={handleRetry}
      />
    );
  } else if (credentials.length === 0) {
    body = (
      <EmptyState
        variant='empty'
        title='No credentials yet'
        description='Create a credential so the Snowflake connector can authenticate with this portfolio.'
      />
    );
  } else {
    body = (
      <Table aria-label='Connector credentials'>
        <Table.Header>
          {TABLE_COLUMNS.map(col => (
            <Table.Head
              key={col.id}
              id={col.id}
              label={col.label}
              isRowHeader={col.id === 'credential'}
            />
          ))}
        </Table.Header>
        <Table.Body>
          {credentials.map(credential => (
            <CredentialRow
              key={credential.id}
              credential={credential}
              displayTimezone={displayTimezone}
              onRevoke={handleRequestRevoke}
            />
          ))}
        </Table.Body>
      </Table>
    );
  }

  return (
    <section className='mb-8'>
      <div className='flex items-center justify-between gap-4 mb-4'>
        <div>
          <h2 className='text-heading-2 text-[var(--color-text-primary)]'>
            Connector credentials
          </h2>
          <p className='text-sm text-[var(--color-text-tertiary)] mt-0.5'>
            Credentials the Snowflake connector uses to authenticate with this
            portfolio.
          </p>
        </div>
        <Button
          color='primary'
          size='sm'
          onClick={handleCreate}
          isDisabled={createMutation.isPending}
          isLoading={createMutation.isPending}
        >
          Create credential
        </Button>
      </div>
      {body}
      <ConfirmationDialog
        isOpen={revokeCredentialId !== null}
        variant='destructive'
        title='Revoke this credential?'
        description={REVOKE_DIALOG_DESCRIPTION}
        confirmLabel='Revoke credential'
        instance='revoke-credential'
        isLoading={revokeMutation.isPending}
        onConfirm={handleConfirmRevoke}
        onCancel={handleCancelRevoke}
      />
      <WebhookSecretCreatedModal
        isOpen={!!secretData}
        onClose={handleCloseSecretModal}
        data={secretData}
        instruction={SECRET_MODAL_INSTRUCTION}
      />
    </section>
  );
}
